import React, { useState, useEffect, useCallback, useRef } from 'react';

interface TypewriterTextProps {
  text: string;
  speed?: number;
}

export const TypewriterText: React.FC<TypewriterTextProps> = ({ 
  text, 
  speed = 10
}) => {
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const textRef = useRef(text);
  const timeoutRef = useRef<number>();

  // Safely process text chunks
  const processTextChunk = useCallback((chunk: string): string => {
    try {
      // Handle special characters and emojis safely
      return chunk.normalize('NFC');
    } catch (error) {
      console.error('Error processing text chunk:', error);
      return chunk;
    }
  }, []);

  const typeNextCharacter = useCallback(() => {
    try {
      if (currentIndex < textRef.current.length) {
        // Process text in chunks of 3 characters
        const nextChars = textRef.current.slice(currentIndex, currentIndex + 3);
        const processedChars = processTextChunk(nextChars);
        
        setDisplayText(prev => {
          try {
            return prev + processedChars;
          } catch (error) {
            console.error('Error updating display text:', error);
            return prev;
          }
        });
        
        setCurrentIndex(prev => Math.min(prev + 3, textRef.current.length));
      }
    } catch (error) {
      console.error('Error in typeNextCharacter:', error);
      // In case of error, display full text
      setDisplayText(textRef.current);
      setCurrentIndex(textRef.current.length);
    }
  }, [currentIndex, processTextChunk]);

  // Reset when text changes
  useEffect(() => {
    textRef.current = text;
    setDisplayText('');
    setCurrentIndex(0);
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }
  }, [text]);

  // Handle typing animation
  useEffect(() => {
    if (currentIndex < textRef.current.length) {
      timeoutRef.current = window.setTimeout(typeNextCharacter, speed);
      
      return () => {
        if (timeoutRef.current) {
          window.clearTimeout(timeoutRef.current);
        }
      };
    }
  }, [currentIndex, speed, typeNextCharacter]);

  // Fallback in case of errors
  if (!text) {
    return null;
  }

  return (
    <div className="font-mono relative break-words whitespace-pre-wrap">
      {displayText}
      {currentIndex < textRef.current.length && (
        <span className="inline-block w-2 h-4 ml-1 bg-blue-500 animate-pulse" />
      )}
    </div>
  );
};